import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Upload, X, FileText, Calendar, Building2, Receipt, Hash } from 'lucide-react';
import { SaudiRiyalIcon } from '@/components/ui/saudi-riyal-icon';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMedicalProviders } from '@/lib/firebase/queries/medicalProviders';
import { toast } from 'sonner';

export interface PendingInvoice {
  /** Attached file. When null the entry is "numbers-only" — handlers
   *  must skip the upload step and persist an empty url/name. */
  file: File | null;
  providerId: string;
  providerName: string;
  providerNameAr: string;
  vatNumber: string;
  invoiceDate: string;
  invoiceNumber: string;
  totalBeforeDiscount: number;
  totalAfterDiscount: number;
  taxableAmount: number;
  nonTaxableAmount: number;
  taxAmount: number;
  thumbnailUrl?: string;
}

interface InvoiceUploaderProps {
  invoices: PendingInvoice[];
  onInvoicesChange: (invoices: PendingInvoice[]) => void;
  translationPrefix: string;
  nationalId?: string;
}

export function InvoiceUploader({ 
  invoices, 
  onInvoicesChange,
  translationPrefix,
  nationalId 
}: InvoiceUploaderProps) {
  const { t, dir, language } = useLanguage();
  const { providers } = useMedicalProviders();
  const activeProviders = providers.filter(p => p.isActive);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    providerId: '',
    vatNumber: '',
    invoiceDate: '',
    invoiceNumber: '',
    totalBeforeDiscount: '',
    totalAfterDiscount: '',
    taxableAmount: '',
    nonTaxableAmount: '',
    taxAmount: '',
  });

  const selectedProvider = activeProviders.find(p => p.id === formData.providerId);

  // VAT applies only if national ID doesn't start with 1 (non-Saudi)
  const vatApplies = nationalId ? !nationalId.startsWith('1') : false;
  const VAT_RATE = 0.15;

  const calculateTax = (taxableAmount: string) => {
    if (!vatApplies) return '0';
    const amount = Number(taxableAmount) || 0;
    return (amount * VAT_RATE).toFixed(2);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('common.fileSizeError'));
      return;
    }

    setSelectedFile(file);
    
    // Create preview URL for images/PDFs
    if (file.type.startsWith('image/') || file.type === 'application/pdf') {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
    
    setFormData({
      providerId: '',
      vatNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      invoiceNumber: '',
      totalBeforeDiscount: '',
      totalAfterDiscount: '',
      taxableAmount: '',
      taxAmount: '',
    });
    setIsModalOpen(true);
    
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleProviderChange = (providerId: string) => {
    const provider = activeProviders.find(p => p.id === providerId);
    setFormData(prev => ({
      ...prev,
      providerId,
      vatNumber: provider?.vatNumber || '',
    }));
  };

  // Open the form modal without a file (numbers-only entry).
  const openNumbersOnly = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData({
      providerId: '',
      vatNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      invoiceNumber: '',
      totalBeforeDiscount: '',
      totalAfterDiscount: '',
      taxableAmount: '',
      nonTaxableAmount: '',
      taxAmount: '',
    });
    setIsModalOpen(true);
  };

  const handleAddInvoice = () => {
    // File is optional now — "numbers-only" entries are allowed.
    if (!formData.providerId || !formData.invoiceDate || !formData.invoiceNumber || !formData.totalBeforeDiscount || !formData.totalAfterDiscount) {
      toast.error(t(`${translationPrefix}.validation.required`));
      return;
    }

    const provider = activeProviders.find(p => p.id === formData.providerId);
    const newInvoice: PendingInvoice = {
      file: selectedFile,
      providerId: formData.providerId,
      providerName: provider?.name || '',
      providerNameAr: provider?.nameAr || '',
      vatNumber: formData.vatNumber,
      invoiceDate: formData.invoiceDate,
      invoiceNumber: formData.invoiceNumber,
      totalBeforeDiscount: Number(formData.totalBeforeDiscount) || 0,
      totalAfterDiscount: Number(formData.totalAfterDiscount) || 0,
      taxableAmount: Number(formData.taxableAmount) || 0,
      nonTaxableAmount: Number(formData.nonTaxableAmount) || 0,
      taxAmount: Number(formData.taxAmount) || 0,
      thumbnailUrl: previewUrl || undefined,
    };

    onInvoicesChange([...invoices, newInvoice]);
    // Don't revoke the preview URL since we're using it as thumbnail
    setIsModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData({
      providerId: '',
      vatNumber: '',
      invoiceDate: '',
      invoiceNumber: '',
      totalBeforeDiscount: '',
      totalAfterDiscount: '',
      taxableAmount: '',
      taxAmount: '',
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setFormData({
      providerId: '',
      vatNumber: '',
      invoiceDate: '',
      invoiceNumber: '',
      totalBeforeDiscount: '',
      totalAfterDiscount: '',
      taxableAmount: '',
      taxAmount: '',
    });
  };

  const handleRemoveInvoice = (index: number) => {
    const newInvoices = invoices.filter((_, i) => i !== index);
    onInvoicesChange(newInvoices);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatCurrency = (amount: number) => {
    return <span className="inline-flex items-center gap-1">{amount.toLocaleString('en-US')} <SaudiRiyalIcon className="h-4 w-4" /></span>;
  };

  return (
    <div>
      {/* Add Invoice Button */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        className="hidden"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          className="gap-2"
        >
          <Upload className="h-4 w-4" />
          {t(`${translationPrefix}.addInvoice`)}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={openNumbersOnly}
          className="gap-2"
        >
          <Receipt className="h-4 w-4" />
          {t(`${translationPrefix}.addInvoiceNumbersOnly`)}
        </Button>
      </div>

      {/* Add Invoice Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) handleCloseModal(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh]" dir={dir}>
          <DialogHeader dir={dir}>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              {t(`${translationPrefix}.addInvoiceTitle`)}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-4">
            {/* Preview Section - 2/3 width */}
            <div className="col-span-2 space-y-2">
              <Label>{t(`${translationPrefix}.preview`)}</Label>
              <div className="border rounded-lg h-[500px] flex items-center justify-center bg-muted/30 overflow-hidden">
                {previewUrl ? (
                  selectedFile?.type === 'application/pdf' ? (
                    <iframe src={previewUrl} className="w-full h-full" title="PDF Preview" />
                  ) : (
                    <img src={previewUrl} alt="Preview" className="max-w-full max-h-full object-contain" />
                  )
                ) : (
                  <div className="text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2" />
                    {selectedFile ? (
                      <>
                        <p className="text-sm">{selectedFile.name}</p>
                        <p className="text-xs">{formatFileSize(selectedFile.size)}</p>
                      </>
                    ) : (
                      <p className="text-sm">{t(`${translationPrefix}.addInvoiceNumbersOnly`)}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Form Section */}
            <div className="space-y-3">
              {/* Provider */}
              <div className="space-y-1">
                <Label>{t(`${translationPrefix}.provider`)} *</Label>
                <Select
                  value={formData.providerId}
                  onValueChange={handleProviderChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t(`${translationPrefix}.selectProvider`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {activeProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {language === 'ar' ? (provider.nameAr || provider.name) : provider.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* VAT Number */}
              <div className="space-y-1">
                <Label>{t(`${translationPrefix}.vatNumber`)}</Label>
                <Input
                  value={formData.vatNumber}
                  placeholder={t(`${translationPrefix}.vatNumberPlaceholder`)}
                  dir="ltr"
                  readOnly
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                {/* Invoice Date */}
                <div className="space-y-1">
                  <Label>{t(`${translationPrefix}.invoiceDate`)} *</Label>
                  <Input
                    type="date"
                    value={formData.invoiceDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoiceDate: e.target.value }))}
                    required
                    dir="ltr"
                  />
                </div>

                {/* Invoice Number */}
                <div className="space-y-1">
                  <Label>{t(`${translationPrefix}.invoiceNumber`)} *</Label>
                  <Input
                    value={formData.invoiceNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                    placeholder="#"
                    required
                    dir="ltr"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {/* Total Before Discount */}
                <div className="space-y-1">
                  <Label>{t(`${translationPrefix}.totalBeforeDiscount`)} *</Label>
                  <Input
                    type="number"
                    value={formData.totalBeforeDiscount}
                    onChange={(e) => setFormData(prev => ({ ...prev, totalBeforeDiscount: e.target.value }))}
                    placeholder="0.00"
                    dir="ltr"
                    required
                  />
                </div>

                {/* Total After Discount */}
                <div className="space-y-1">
                  <Label>{t(`${translationPrefix}.totalAfterDiscount`)} *</Label>
                  <Input
                    type="number"
                    value={formData.totalAfterDiscount}
                    onChange={(e) => {
                      const newTotal = e.target.value;
                      const totalN = Number(newTotal) || 0;
                      // Clamp non-taxable to [0, total] so the invariant
                      //   taxable + nonTaxable == totalAfterDiscount
                      // holds even when shrinking the total.
                      const clampedNonTax = Math.min(Number(formData.nonTaxableAmount) || 0, totalN);
                      const newTaxable = String(Math.max(0, totalN - clampedNonTax));
                      setFormData(prev => ({
                        ...prev,
                        totalAfterDiscount: newTotal,
                        nonTaxableAmount: String(clampedNonTax),
                        taxableAmount: newTaxable,
                        taxAmount: calculateTax(newTaxable),
                      }));
                    }}
                    placeholder="0.00"
                    dir="ltr"
                    required
                  />
                </div>
              </div>

              {/* Non-VAT Amount + Taxable Amount split (always sums to totalAfterDiscount) */}
              {vatApplies && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label>{t(`${translationPrefix}.nonTaxableAmount`)}</Label>
                    <Input
                      type="number"
                      value={formData.nonTaxableAmount}
                      onChange={(e) => {
                        const total = Number(formData.totalAfterDiscount) || 0;
                        // Clamp to [0, total] so taxable can't go negative and totals stay consistent.
                        const clampedNonTax = Math.min(Math.max(0, Number(e.target.value) || 0), total);
                        const newTaxable = String(Math.max(0, total - clampedNonTax));
                        setFormData(prev => ({
                          ...prev,
                          nonTaxableAmount: String(clampedNonTax),
                          taxableAmount: newTaxable,
                          taxAmount: calculateTax(newTaxable),
                        }));
                      }}
                      placeholder="0.00"
                      dir="ltr"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>{t(`${translationPrefix}.taxableAmount`)}</Label>
                    <Input
                      type="number"
                      value={formData.taxableAmount}
                      onChange={(e) => {
                        const total = Number(formData.totalAfterDiscount) || 0;
                        const clampedTaxable = Math.min(Math.max(0, Number(e.target.value) || 0), total);
                        const newNonTax = String(Math.max(0, total - clampedTaxable));
                        setFormData(prev => ({
                          ...prev,
                          taxableAmount: String(clampedTaxable),
                          nonTaxableAmount: newNonTax,
                          taxAmount: calculateTax(String(clampedTaxable)),
                        }));
                      }}
                      placeholder="0.00"
                      dir="ltr"
                    />
                  </div>
                </div>
              )}

              {/* Tax Amount (Auto-calculated) */}
              <div className="space-y-1">
                <Label>{t(`${translationPrefix}.taxAmount`)} {vatApplies ? '(15%)' : ''}</Label>
                <Input
                  type="number"
                  value={formData.taxAmount}
                  placeholder="0.00"
                  dir="ltr"
                  readOnly
                  disabled
                  className="bg-muted"
                />
              </div>

              {/* Grand Total = totalAfterDiscount + taxAmount (read-only) */}
              <div className="space-y-1">
                <Label>{t(`${translationPrefix}.grandTotal`)}</Label>
                <Input
                  type="number"
                  value={((Number(formData.totalAfterDiscount) || 0) + (Number(formData.taxAmount) || 0)).toFixed(2)}
                  dir="ltr"
                  readOnly
                  disabled
                  className="bg-muted font-medium"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCloseModal}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="button" 
              onClick={handleAddInvoice}
              disabled={!formData.providerId || !formData.invoiceDate || !formData.invoiceNumber || !formData.totalBeforeDiscount || !formData.totalAfterDiscount}
            >
              {t(`${translationPrefix}.addInvoiceBtn`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
